import React, { useEffect, useState } from 'react'
import { useRequest } from 'hooks/useRequest'
import AnimatedSpan from 'containers/animatedSpan/AnimatedSpan'
import Spinner from 'components/loader/Spinner'
import { DataTable } from 'primereact/datatable'
import Icons from 'assets/icons'
import { Column } from 'primereact/column'
import useToast from 'hooks/useToast'
import { getPermission } from 'utils/Auth'
import { Dialog } from 'primereact/dialog'
import ApprovalContent from 'components/modalContent/ApprovalContent'
import { useDispatch, useSelector } from 'react-redux'
import { closeApprovalModal, openApprovalModal } from 'redux/features/general/generalSlice'
import Button from 'components/formElements/Button'
import { Dropdown } from 'primereact/dropdown'
import { InputMask } from 'primereact/inputmask'
import DefaultSelect from 'components/formElements/DefaultSelect'
import { roleIds } from 'utils/Constants'

const CrmPersonModal = ({ person, departments, positions, companies, onPersonChange, onSave, onCancel, isEdit }) => {
    const [emailError, setEmailError] = useState('')
    const [hasTriedToSave, setHasTriedToSave] = useState(false)
    const { showToast } = useToast()
    
    const validateEmail = (email) => {
        if (!email) return true // Empty email is valid
        
        // Check for Turkish/special characters first
        const hasTurkishChars = /[çğıöşüÇĞIİÖŞÜ]/.test(email)
        if (hasTurkishChars) {
            return false
        }
        
        // Standard email regex
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        return emailRegex.test(email)
    }
    
    const handleChange = (field, value) => {
        // Email validation logic
        if (field === 'mail') {
            // Filter Turkish characters immediately as user types
            const filteredValue = value.replace(/[çğıöşüÇĞIİÖŞÜ]/g, '');
            
            // Only show error if user has tried to save before or if they're correcting an invalid email
            if (hasTriedToSave && emailError && filteredValue) {
                if (validateEmail(filteredValue)) {
                    setEmailError('')
                }
            }
            
            onPersonChange({ ...person, [field]: filteredValue })
            return
        }
        
        let updatedPerson = { ...person, [field]: value }
        
        // Şirket değiştirildiğinde eski crmManagement yapısını temizle
        if (field === 'crmManagementId') {
            updatedPerson = {
                ...person,
                [field]: value,
                crmManagement: null // Eski yapıyı temizle
            }
        }
        
        onPersonChange(updatedPerson)
    }
    
    const handleSave = () => {
        setHasTriedToSave(true)
        
        // Zorunlu alan kontrolleri
        if (!person?.firsName) {
            showToast('Ad Girin', 'error')
            return
        }
        if (!person?.lastName) {
            showToast('Soyad Girin', 'error')
            return
        }
        if (!person?.crmManagementId) {
            showToast('Şirket Seçin', 'error')
            return
        }
        
        // E-posta validasyonu kontrolü
        if (person?.mail && !validateEmail(person.mail)) {
            setEmailError('Geçerli bir e-posta adresi giriniz (Türkçe karakter kullanılamaz)')
            return
        }
        onSave()
    }
    console.log(person, 'person')
    return (
        <div className='w-100'>
            <div className='w-100 d-flex flex-column gap-3'>
                <div className='row'>
                    <div className='col-md-6'>
                        <div className='form-control'>
                            <label className='form-control-label'>Ad *</label>
                            <input
                                className='default-input'
                                type='text'
                                value={person?.firsName || ''}
                                onChange={e => handleChange('firsName', e.target.value)}
                                placeholder='Ad girin'
                            />
                        </div>
                    </div>
                    <div className='col-md-6'>
                        <div className='form-control'>
                            <label className='form-control-label'>Soyad *</label>
                            <input
                                className='default-input'
                                type='text'
                                value={person?.lastName || ''}
                                onChange={e => handleChange('lastName', e.target.value)}
                                placeholder='Soyad girin'
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-6'>
                        <div className='form-control'>
                            <label className='form-control-label'>E-posta</label>
                            <input
                                className={`default-input ${emailError ? 'error' : ''}`}
                                type='email'
                                value={person?.mail || ''}
                                onChange={e => handleChange('mail', e.target.value)}
                                placeholder='E-posta girin'
                                autoComplete='email'
                            />
                            {emailError && <div className='error-message'>{emailError}</div>}
                        </div>
                    </div>
                    <div className='col-md-6'>
                        <div className='form-control'>
                            <label className='form-control-label'>Telefon</label>
                            <InputMask
                                mask='(999) 999-9999'
                                value={person?.phone || ''}
                                onChange={e => handleChange('phone', e.target.value)}
                                placeholder='(555) 123-4567'
                                className='default-input'
                                autoComplete='tel'
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>LinkedIn</label>
                            <input
                                className='default-input'
                                type='text'
                                value={person?.linkedin || ''}
                                onChange={e => handleChange('linkedin', e.target.value)}
                                placeholder='LinkedIn profili'
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-6'>
                        <div className='form-control'>
                            <label className='form-control-label'>Departman</label>
                            <Dropdown
                                value={person?.crmCompanyDepartmentId || person?.newCrmCompanyDepartmentName}
                                onChange={e => {
                                    if (!e.originalEvent) return
                                    let isId = Number.isInteger(e.value) && departments.find(d => d.value === e.value)
                                    onPersonChange({
                                        ...person,
                                        crmCompanyDepartmentId: isId ? e.value : null,
                                        newCrmCompanyDepartmentName: isId ? null : e.value
                                    })
                                }}
                                onInput={e => {
                                    onPersonChange({
                                        ...person,
                                        crmCompanyDepartmentId: null,
                                        newCrmCompanyDepartmentName: e.target.value
                                    })
                                }}
                                options={departments}
                                optionLabel='label'
                                optionValue='value'
                                placeholder='Departman seçin veya yazın'
                                filter
                                filterLocale='tr'
                                editable
                                filterInputAutoFocus={false}
                                className='w-100'
                            />
                        </div>
                    </div>
                    <div className='col-md-6'>
                        <div className='form-control'>
                            <label className='form-control-label'>Pozisyon</label>
                            <Dropdown
                                value={person?.crmCompanyPositionId || person?.newCrmCompanyPositionName}
                                onChange={e => {
                                    if (!e.originalEvent) return
                                    let isId = Number.isInteger(e.value) && positions.find(p => p.value === e.value)
                                    onPersonChange({
                                        ...person,
                                        crmCompanyPositionId: isId ? e.value : null,
                                        newCrmCompanyPositionName: isId ? null : e.value
                                    })
                                }}
                                onInput={e => {
                                    onPersonChange({
                                        ...person,
                                        crmCompanyPositionId: null,
                                        newCrmCompanyPositionName: e.target.value
                                    })
                                }}
                                options={positions}
                                optionLabel='label'
                                optionValue='value'
                                placeholder='Pozisyon seçin veya yazın'
                                filter
                                filterLocale='tr'
                                editable
                                filterInputAutoFocus={false}
                                className='w-100'
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>Şirket *</label>
                            <Dropdown
                                value={person?.crmManagementId || null}
                                onChange={e => handleChange('crmManagementId', e.value)}
                                options={companies || []}
                                optionLabel='label'
                                optionValue='value'
                                placeholder='Şirket seçin...'
                                filter
                                filterLocale='tr'
                                filterInputAutoFocus={false}
                                className='w-100'
                                showClear
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12 d-flex justify-content-end gap-3'>
                        <Button className='btn-danger' title='İptal' handleClick={onCancel} />
                        <Button className='btn-success' title={isEdit ? 'Güncelle' : 'Kaydet'} handleClick={handleSave} />
                    </div>
                </div>
            </div>
        </div>
    )
}

const CrmDirectory = () => {
    const [request] = useRequest()
    const { showToast } = useToast()
    const dispatch = useDispatch()
    const { general } = useSelector((state) => state)
    const { approvalModal } = general
    const [crmPersons, setCrmPersons] = useState([])
    const [loading, setLoading] = useState(false)
    const [departments, setDepartments] = useState([])
    const [positions, setPositions] = useState([])
    const [upsertPersonModal, setUpsertPersonModal] = useState({ open: false, id: null, person: {} })
    const [allCrmCompanies, setAllCrmCompanies] = useState([])
    const [accountManagers, setAccountManagers] = useState([])
    const [filterOptions, setFilterOptions] = useState({ searchString: '', company: null, position: [], am: [] })

    useEffect(() => {
        getCrmPersons()
        getInitialData()
        getAllCrmCompanies()
        getAccountManagers()
    }, [])

    const getCrmPersons = () => {
        setLoading(true)
        request('/CrmManagement/GetAllCrmPersons', { method: 'GET' })
            .then(res => {
                if (res.data.ok) {
                    setCrmPersons(res.data.result)
                } else {
                    showToast('Kişiler yüklenirken hata oluştu', 'error')
                }
            })
            .catch(error => {
                console.error('Error fetching CRM persons:', error)
                showToast('Kişiler yüklenirken hata oluştu', 'error')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const getInitialData = () => {
        // Get departments from API
        request('/CrmManagement/GetCrmCompanyDepartments', { method: 'GET' })
            .then(res => {
                if (res.data.ok) {
                    setDepartments(res.data.result.map(dept => ({ label: dept.name, value: dept.id })))
                }
            }).catch(error => {
                console.error('Error fetching departments:', error)
            })

        // Get positions from API
        request('/CrmManagement/GetCrmCompanyPositions', { method: 'GET' })
            .then(res => {
                if (res.data.ok) {
                    setPositions(res.data.result.map(pos => ({ label: pos.name, value: pos.id })))
                }
            }).catch(error => {
                console.error('Error fetching positions:', error)
            })
    }

    const getAllCrmCompanies = () => {
        request('/CrmManagement/GetAllCrmForDirectoryCompanies', { method: 'GET' })
            .then(res => {
                if (res.data.ok) {
                    const formattedCompanies = res.data.result.map(company => ({
                        label: company.newCrmCompanyName || company.name,
                        value: company.id
                    }))
                    setAllCrmCompanies(formattedCompanies)
                } else {
                    showToast('Şirketler yüklenirken hata oluştu', 'error')
                }
            })
            .catch(error => {
                console.error('Error fetching CRM companies:', error)
                showToast('Şirketler yüklenirken hata oluştu', 'error')
            })
    }

    const getAccountManagers = () => {
        request('/Account/GetAllUsers', { method: 'GET',params: {
            isDeleteFilter: true
        } })
            .then(res => {
                if (res.data.ok) {
                    setAccountManagers(res.data.result.filter(r => r.roles.map(r => r.id).includes(roleIds.accountManager)))
                } else {
                    showToast('Account Manager\'lar yüklenirken hata oluştu', 'error')
                }
            })
            .catch(error => {
                console.error('Error fetching account managers:', error)
                showToast('Account Manager\'lar yüklenirken hata oluştu', 'error')
            })
    }

    const deleteButton = (deleteData, deleteEndpoint, tableForDelete) => {
        return (
            <Icons.deleteNotFill
                onClick={() => {
                    handleDeleteRow(deleteData, deleteEndpoint, tableForDelete)
                }}
                size={20}
                className='text-danger cursor-pointer'
            />
        )
    }

    const handleDeleteRow = (deleteData, deleteEndpoint, tableForDelete) => {
        dispatch(openApprovalModal({ open: true, content: 'Silmek istediğinize emin misiniz?', deleteData, deleteEndpoint, tableForDelete }))
    }

    const handleApprovalModalApproved = () => {
        if (approvalModal.deleteEndpoint) {
            handleCloseApprovalModal()
            handleDeleteCrmPerson(approvalModal.deleteData)
        }
    }

    const handleCloseApprovalModal = () => {
        dispatch(closeApprovalModal())
    }

    const handleDeleteCrmPerson = (personData) => {
        const deleteData = {
            id: personData.id,
            firsName: personData.firsName || "",
            lastName: personData.lastName || "",
            phone: personData.phone || "",
            mail: personData.mail || "",
            linkedin: personData.linkedin || "",
            crmPersonStatus: personData.crmPersonStatus || 0,
            crmManagementId: personData.crmManagementId || 0,
            crmCompanyDepartmentId: personData.crmCompanyDepartmentId || 0,
            newCrmCompanyDepartmentName: personData.newCrmCompanyDepartmentName || "",
            crmCompanyPositionId: personData.crmCompanyPositionId || 0,
            newCrmCompanyPositionName: personData.newCrmCompanyPositionName || ""
        }

        request('/CrmManagement/DeleteCrmPerson', {
            method: 'DELETE',
            data: deleteData
        }).then((res) => {
            if (res.data.ok) {
                showToast('Kişi Silindi', 'success')
                getCrmPersons() // Refresh persons
            } else {
                showToast('Silme işlemi sırasında hata oluştu', 'error')
            }
        }).catch(error => {
            showToast('Silme işlemi sırasında hata oluştu', 'error')
            console.error('Error deleting CRM person:', error)
        })
    }

    const handleAddCrmPerson = () => {
        if (!upsertPersonModal.person?.firsName) {
            showToast('Ad Girin', 'error')
            return
        }
        if (!upsertPersonModal.person?.lastName) {
            showToast('Soyad Girin', 'error')
            return
        }

        // crmManagementId değerini doğru şekilde belirle
        let crmManagementId = 0;
        if (upsertPersonModal.person.crmManagementId) {
            crmManagementId = upsertPersonModal.person.crmManagementId;
        } else if (upsertPersonModal.person.crmManagement?.crmCompany?.id) {
            crmManagementId = upsertPersonModal.person.crmManagement.crmCompany.id;
        }

        const postData = {
            id: upsertPersonModal.id || 0,
            firsName: upsertPersonModal.person.firsName,
            lastName: upsertPersonModal.person.lastName,
            phone: upsertPersonModal.person.phone || "",
            mail: upsertPersonModal.person.mail || "",
            linkedin: upsertPersonModal.person.linkedin || "",
            crmPersonStatus: upsertPersonModal.person.crmPersonStatus || 0,
            crmManagementId: crmManagementId,
            crmCompanyDepartmentId: upsertPersonModal.person.crmCompanyDepartmentId || 0,
            newCrmCompanyDepartmentName: upsertPersonModal.person.newCrmCompanyDepartmentName || "",
            crmCompanyPositionId: upsertPersonModal.person.crmCompanyPositionId || 0,
            newCrmCompanyPositionName: upsertPersonModal.person.newCrmCompanyPositionName || ""
        }

        request('/CrmManagement/UpsertCrmPerson', {
            method: 'POST',
            data: postData
        }).then((res) => {
            if (res.data.ok) {
                showToast(upsertPersonModal.id ? 'Kişi Güncellendi' : 'Kişi Eklendi', 'success')
                setUpsertPersonModal({ open: false, id: null, person: {} })
                getCrmPersons() // Refresh persons
            } else {
                showToast('İşlem sırasında hata oluştu', 'error')
            }
        }).catch(error => {
            showToast('İşlem sırasında hata oluştu', 'error')
            console.error('Error saving CRM person:', error)
        })
    }

    // Filtreleme mantığı
    const filteredCrmPersons = crmPersons.filter((person) => {
        const keys = Object.keys(filterOptions)
        let rt = [true]
        
        keys.forEach((k) => {
            if (filterOptions[k] != null && filterOptions[k] !== '' && !(Array.isArray(filterOptions[k]) && filterOptions[k].length === 0)) {
                if (k === 'searchString') {
                    const fullName = `${person.firsName || ''} ${person.lastName || ''}`.toLowerCase('tr')
                    rt.push(fullName.includes(filterOptions[k].toLowerCase('tr')))
                } else if (k === 'company') {
                    rt.push(person.crmManagement?.crmCompany?.id === filterOptions[k])
                } else if (k === 'position') {
                    const selectedPositionIds = filterOptions[k].map(p => p.value)
                    rt.push(selectedPositionIds.includes(person.crmCompanyPositionId))
                } else if (k === 'am') {
                    const selectedAmIds = filterOptions[k].map(am => am.value)
                    const personAmIds = person?.crmManagement?.crmAccountManagers?.map(am => am.accountManagerUserId) || []
                    const hasMatchingAm = personAmIds.some(amId => selectedAmIds.includes(amId))
                    rt.push(hasMatchingAm)
                }
            }
        })
        
        return rt.every((r) => r)
    })

    const handleFilterChange = (value, field) => {
        setFilterOptions(prev => ({ ...prev, [field]: value }))
    }

    return (
        <AnimatedSpan className='page-container d-flex flex-column gap-4'>
            <div className='page-header'>
                <h2>CRM Rehber</h2>
            </div>

            {loading ? (
                <div className='d-flex w-100 justify-content-center'>
                    <Spinner size={3} />
                </div>
            ) : (
                <div className='row'>
                    <div className='col-md-12'>
                        <DataTable
                            emptyMessage='Hiç Kayıt Bulunamadı'
                            responsiveLayout='stack'
                            dataKey='id'
                            header={
                                <div className='d-flex justify-content-between align-items-center gap-3'>
                                    <div className='row flex-1'>
                                        <div className='col-md-3'>
                                            <span className='d-flex align-items-center gap-2'>
                                                <Icons.search size={18} />
                                                <input
                                                    className='default-input'
                                                    placeholder='Ad Soyad Ara...'
                                                    value={filterOptions.searchString}
                                                    onChange={(e) => handleFilterChange(e.target.value, 'searchString')}
                                                />
                                            </span>
                                        </div>
                                        <div className='col-md-3'>
                                            <DefaultSelect
                                                handleChange={(e) => handleFilterChange(e ? e.value : null, 'company')}
                                                value={filterOptions.company ? 
                                                    allCrmCompanies.find(c => c.value === filterOptions.company) : null}
                                                placeholder='Şirket'
                                                isSearchable
                                                isClearable
                                                selectOptions={allCrmCompanies}
                                            />
                                        </div>
                                        <div className='col-md-2'>
                                            <DefaultSelect
                                                handleChange={(e) => handleFilterChange(e || [], 'position')}
                                                value={filterOptions.position}
                                                placeholder='Pozisyon'
                                                isSearchable
                                                isMulti
                                                isClearable
                                                selectOptions={positions}
                                            />
                                        </div>
                                        <div className='col-md-3'>
                                            <DefaultSelect
                                                handleChange={(e) => handleFilterChange(e || [], 'am')}
                                                value={filterOptions.am}
                                                placeholder='Account Manager'
                                                isSearchable
                                                isMulti
                                                isClearable
                                                selectOptions={accountManagers.map(am => ({ 
                                                    label: `${am.firstName} ${am.lastName}`, 
                                                    value: am.id 
                                                }))}
                                            />
                                        </div>
                                    </div>
                                    <div className='d-flex justify-content-end'>
                                        {getPermission('CrmManagement.update') && <Button className='btn-primary btn-sm' title='Kişi Ekle' handleClick={() => setUpsertPersonModal({ open: true, id: null, person: {} })} icon={<Icons.plus />} />}
                                    </div>
                                </div>
                            }
                            paginator
                            alwaysShowPaginator={false}
                            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            currentPageReportTemplate="Toplam {totalRecords} kayıttan {first} - {last} arasındakiler gösteriliyor"
                            rows={25}
                            value={filteredCrmPersons}
                            rowClassName={`cursor-pointer table-hover`}
                            cellClassName={`text-sm p-2`}
                            onRowClick={e => {
                                if (e.originalEvent.target.className.includes('deleteNotFill') || e.originalEvent.target.className.includes('text-danger')) return
                                if (getPermission('CrmManagement.update') && e.data.id) {
                                    setUpsertPersonModal({ open: true, id: e.data.id, person: e.data })
                                }
                            }}
                        >
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='firsName'
                                style={{ width: 'auto' }}
                                header='İsim'
                                sortable
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='lastName'
                                style={{ width: 'auto' }}
                                header='Soyisim'
                                sortable
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='crmCompanyDepartmentId'
                                style={{ width: 'auto' }}
                                header='Departman'
                                sortable
                                sortFunction={(event) => {
                                    const { data, order } = event;
                                    return data.sort((a, b) => {
                                        const aLabel = departments.find(d => d.value === a.crmCompanyDepartmentId)?.label || a.newCrmCompanyDepartmentName || '';
                                        const bLabel = departments.find(d => d.value === b.crmCompanyDepartmentId)?.label || b.newCrmCompanyDepartmentName || '';
                                        return order * aLabel.localeCompare(bLabel, 'tr', { numeric: true });
                                    });
                                }}
                                body={(rowData) => {
                                    const departmentLabel = departments.find(d => d.value === rowData.crmCompanyDepartmentId)?.label || rowData.newCrmCompanyDepartmentName || '-'
                                    return departmentLabel
                                }}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='crmCompanyPositionId'
                                style={{ width: 'auto' }}
                                header='Pozisyon'
                                sortable
                                sortFunction={(event) => {
                                    const { data, order } = event;
                                    return data.sort((a, b) => {
                                        const aLabel = positions.find(p => p.value === a.crmCompanyPositionId)?.label || a.newCrmCompanyPositionName || '';
                                        const bLabel = positions.find(p => p.value === b.crmCompanyPositionId)?.label || b.newCrmCompanyPositionName || '';
                                        return order * aLabel.localeCompare(bLabel, 'tr', { numeric: true });
                                    });
                                }}
                                body={(rowData) => {
                                    const positionLabel = positions.find(p => p.value === rowData.crmCompanyPositionId)?.label || rowData.newCrmCompanyPositionName || '-'
                                    return positionLabel
                                }}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='crmCompanyName'
                                style={{ width: 'auto' }}
                                header='Şirket'
                                sortable
                                sortFunction={(event) => {
                                    const { data, order } = event;
                                    return data.sort((a, b) => {
                                        const aCompany = a?.crmManagement?.crmCompany?.name || a?.crmCompanyName || '';
                                        const bCompany = b?.crmManagement?.crmCompany?.name || b?.crmCompanyName || '';
                                        return order * aCompany.localeCompare(bCompany, 'tr', { numeric: true });
                                    });
                                }}
                                body={(rowData) => {
                                    const companyName =  rowData?.crmManagement?.crmCompany?.name || 
                                                       rowData?.crmCompanyName || '-'
                                    return companyName
                                }}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='crmManagement.crmAccountManagers'
                                style={{ width: 'auto' }}
                                header='Account Manager'
                                sortable={false}
                                body={(rowData) => {
                                    const amIds = rowData?.crmManagement?.crmAccountManagers?.map(am => am.accountManagerUserId) || []
                                    if (amIds.length === 0) return '-'
                                    
                                    const amNames = amIds.map(amId => {
                                        const accountManager = accountManagers.find(am => am.id === amId)
                                        return accountManager ? `${accountManager.firstName} ${accountManager.lastName}` : 'Bilinmeyen'
                                    })
                                    
                                    return amNames.join(', ')
                                }}
                            />
                            <Column
                                field='action'
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold pl-0'
                                style={{ width: '2.5rem' }}
                                body={(rowData) => (
                                    <span className='d-flex gap-1'>
                                        {getPermission('CrmManagement.update') && deleteButton(rowData, '/CrmManagement/DeleteCrmPerson', 'persons')}
                                    </span>
                                )}
                            />
                        </DataTable>
                    </div>
                </div>
            )}

            <Dialog draggable={false} visible={approvalModal.open} onHide={handleCloseApprovalModal}>
                <ApprovalContent handleClose={handleCloseApprovalModal} handleApproved={handleApprovalModalApproved} />
            </Dialog>
            <Dialog style={{ width: '600px', maxWidth: '100%' }} draggable={false} header={upsertPersonModal.id ? 'Kişi Düzenle' : 'Kişi Ekle'} visible={upsertPersonModal.open} onHide={() => setUpsertPersonModal({ open: false, id: null, person: {} })}>
                <CrmPersonModal 
                    person={upsertPersonModal.person}
                    departments={departments}
                    positions={positions}
                    companies={allCrmCompanies}
                    onPersonChange={(person) => setUpsertPersonModal(prev => ({ ...prev, person }))}
                    onSave={handleAddCrmPerson}
                    onCancel={() => setUpsertPersonModal({ open: false, id: null, person: {} })}
                    isEdit={!!upsertPersonModal.id}
                />
            </Dialog>
        </AnimatedSpan>
    )
}

export default CrmDirectory
